import React, { useCallback, useEffect, useState } from 'react';
import TreeView from '@material-ui/lab/TreeView';
import TreeItem from '@material-ui/lab/TreeItem';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import Select from 'react-select';
import { Button as ChakraButton } from '@chakra-ui/react';
import { Button } from 'reactstrap';
import Swal from 'sweetalert2';

import { FolderMap } from '../../../models/Folder';

interface SelectOption {
    value: string;
    label: string;
}

const AUTH_OPTIONS: SelectOption[] = [
    { value: '01', label: '01 - Upload File' },
    { value: '02', label: '02 - Change Information' },
    { value: '03', label: '03 - View File' },
    { value: '04', label: '04 - Delete File' },
    { value: '05', label: '05 - Copy Link' },
    { value: '06', label: '06 - Move Link' },
];

const selectMenuStyle = { menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) };

interface Props {
    sid: string;
    addedNodeCodes: string[];
    onConfirm: (selections: { node_code: string; folder_name: string }[], authIds: string) => void;
    onCancel: () => void;
}

function findNode(nodes: FolderMap[], nodeCode: string): FolderMap | null {
    for (const n of nodes) {
        if (n.node_code === nodeCode) return n;
        if (n.list_node?.length) {
            const found = findNode(n.list_node, nodeCode);
            if (found) return found;
        }
    }
    return null;
}

function collectNodeIds(nodes: FolderMap[]): string[] {
    return nodes.map(n => n.node_code);
}

function FolderTreePicker({ sid, addedNodeCodes, onConfirm, onCancel }: Props) {
    const [tree, setTree] = useState<FolderMap[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [expanded, setExpanded] = useState<string[]>([]);
    const [checked, setChecked] = useState<string[]>([]);
    const [authSelected, setAuthSelected] = useState<SelectOption[]>([]);

    const token = () => localStorage.getItem('DMS_APPLICATION') ?? '';

    useEffect(() => {
        if (!sid) return;
        setIsLoading(true);
        fetch(`/v1/folder/map?app_id=${sid}`, {
            headers: { Authorization: `Bearer ${token()}` },
        })
            .then(r => r.json())
            .then((data: FolderMap[]) => {
                const nodes = (data ?? []).filter(n => n.sid === sid);
                setTree(nodes);
                setExpanded(collectNodeIds(nodes));
            })
            .catch(() => setTree([]))
            .finally(() => setIsLoading(false));
    }, [sid]);

    const handleCheck = useCallback((nodeCode: string, isChecked: boolean) => {
        setChecked(prev =>
            isChecked ? [...prev, nodeCode] : prev.filter(c => c !== nodeCode)
        );
    }, []);

    const handleAdd = () => {
        if (checked.length === 0) {
            Swal.fire('กรุณาเลือก Folder อย่างน้อย 1 รายการ', '', 'warning');
            return;
        }
        if (authSelected.length === 0) {
            Swal.fire('กรุณาเลือก Auth IDs', '', 'warning');
            return;
        }
        const authIds = authSelected.map(a => a.value).sort().join('|');
        const selections = checked.map(nc => {
            const node = findNode(tree, nc);
            return { node_code: nc, folder_name: node?.folder_name ?? nc };
        });
        onConfirm(selections, authIds);
    };

    const renderTree = (nodes: FolderMap[]): React.ReactNode =>
        nodes.map(node => {
            const isAdded = addedNodeCodes.includes(node.node_code);
            const isChecked = checked.includes(node.node_code);

            const label = (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input
                        type="checkbox"
                        checked={isChecked}
                        disabled={isAdded}
                        onChange={e => handleCheck(node.node_code, e.target.checked)}
                        onClick={e => e.stopPropagation()}
                        style={{ cursor: isAdded ? 'not-allowed' : 'pointer', flexShrink: 0 }}
                    />
                    <span style={{ fontSize: 13, color: isAdded ? '#aaa' : undefined }}>
                        {node.folder_name}
                        {isAdded && (
                            <span style={{ marginLeft: 6, fontSize: 11, color: '#aaa' }}>
                                (added)
                            </span>
                        )}
                    </span>
                </div>
            );

            return (
                <TreeItem key={node.node_code} nodeId={node.node_code} label={label}>
                    {node.list_node?.length > 0 && renderTree(node.list_node)}
                </TreeItem>
            );
        });

    return (
        <div className="border p-3 mt-3 rounded bg-light">
            <h6>เลือก Folder</h6>

            {isLoading ? (
                <div className="text-center py-3 text-muted">กำลังโหลด...</div>
            ) : tree.length === 0 ? (
                <div className="text-center py-3 text-muted">ไม่พบ folder</div>
            ) : (
                <div
                    style={{
                        maxHeight: 280,
                        overflowY: 'auto',
                        border: '1px solid #ddd',
                        borderRadius: 4,
                        padding: 8,
                        background: '#fff',
                    }}
                >
                    <TreeView
                        defaultCollapseIcon={<ArrowDropDownIcon />}
                        defaultExpandIcon={<ArrowRightIcon />}
                        expanded={expanded}
                        onNodeToggle={(_, nodeIds) => setExpanded(nodeIds)}
                    >
                        {renderTree(tree)}
                    </TreeView>
                </div>
            )}

            <div className="form-group mt-3">
                <label>
                    Auth IDs <span style={{ color: 'red' }}>*</span>
                </label>
                <Select
                    menuPortalTarget={document.body}
                    styles={selectMenuStyle}
                    isMulti
                    placeholder="เลือกสิทธิ์"
                    options={AUTH_OPTIONS}
                    value={authSelected}
                    onChange={val => setAuthSelected(val as SelectOption[])}
                />
            </div>

            <div className="mt-2" style={{ display: 'flex', gap: 8 }}>
                <Button
                    color="primary"
                    size="sm"
                    onClick={handleAdd}
                    disabled={checked.length === 0 || authSelected.length === 0}
                >
                    Add ({checked.length} folder{checked.length !== 1 ? 's' : ''})
                </Button>
                <ChakraButton colorScheme="red" size="sm" onClick={onCancel}>
                    Cancel
                </ChakraButton>
            </div>
        </div>
    );
}

export default FolderTreePicker;
